import { isEqual } from 'lodash'
import { z } from 'zod'

const RECOVERY_KEY_STORAGE_TTL_MS = 24 * 60 * 60 * 1000

// `storage` is the browser's native event: it fires in every other tab when localStorage changes, but never in the
// tab that made the change, so a write here dispatches one for this tab too.
export const dispatchRecoveryKeyStorageEvent = (storageKey: string) => {
  window.dispatchEvent(new StorageEvent('storage', { key: storageKey, storageArea: localStorage }))
}

export const subscribeToRecoveryKeyStorageEvent = (
  onChange: () => void,
  options?: Pick<AddEventListenerOptions, 'signal'>,
) => {
  window.addEventListener('storage', onChange, options)

  return () => window.removeEventListener('storage', onChange)
}

const StoredRecoveryKeySchema = z.object({
  recoveryKey: z.string(),
  expiresAt: z.number(),
})

export const getRecoveryKeyStorageKey = (userId: number) => `ai-recovery-key:${userId}`

const encodeStoredRecoveryKey = (recoveryKey: string) =>
  JSON.stringify(
    StoredRecoveryKeySchema.parse({
      recoveryKey,
      expiresAt: Date.now() + RECOVERY_KEY_STORAGE_TTL_MS,
    }),
  )

export const decodeStoredRecoveryKey = (storedValue: string) => {
  try {
    return StoredRecoveryKeySchema.parse(JSON.parse(storedValue))
  } catch {
    return null
  }
}

let cachedRecoveryKey: z.infer<typeof StoredRecoveryKeySchema> | null = null

export const getRecoveryKey = (userId: number) => {
  const storageKey = getRecoveryKeyStorageKey(userId)

  const storedValue = localStorage.getItem(storageKey)

  if (storedValue === null) {
    return null
  }

  const recoveryKey = decodeStoredRecoveryKey(storedValue)

  if (!isEqual(recoveryKey, cachedRecoveryKey)) {
    cachedRecoveryKey = recoveryKey
  }

  return cachedRecoveryKey
}

export const updateRecoveryKey = (userId: number, recoveryKey?: string | null) => {
  const storageKey = getRecoveryKeyStorageKey(userId)

  if (recoveryKey === null || recoveryKey === undefined) {
    localStorage.removeItem(storageKey)
  } else {
    localStorage.setItem(storageKey, encodeStoredRecoveryKey(recoveryKey))
  }
  dispatchRecoveryKeyStorageEvent(storageKey)
}
